import React, { useEffect, useState } from 'react';
import { HiCalendarDays, HiBuildingOffice, HiArrowRightOnRectangle, HiExclamationTriangle } from 'react-icons/hi2';
import { IoMdMicrophone } from 'react-icons/io';
import Input from './ui/Input';
import { submitIssueReport } from '../services/issueReportService';
import logo from '../assets/logo.svg';

const ISSUE_FREQUENCY = {
    always: { value: 'always', label: 'Always' },
    sometimes: { value: 'sometimes', label: 'Sometimes' },
    once: { value: 'once', label: 'Only once' },
};

/**
 * Left drawer for switching between calendar, events, and rooms occupancy views.
 */
export default function SchedulePrimaryViewDrawer({
    isOpen,
    isAnimating,
    onClose,
    timeMode,
    onSelectPrimaryView,
    onChangeInstitution,
}) {
    const [drawerScreen, setDrawerScreen] = useState('menu');
    const [tryingToDo, setTryingToDo] = useState('');
    const [expectedBehavior, setExpectedBehavior] = useState('');
    const [actualBehavior, setActualBehavior] = useState('');
    const [issueFrequency, setIssueFrequency] = useState('sometimes');
    const [reproSteps, setReproSteps] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [reportSending, setReportSending] = useState(false);
    const [reportError, setReportError] = useState(null);
    const [reportSent, setReportSent] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setDrawerScreen('menu');
            setTryingToDo('');
            setExpectedBehavior('');
            setActualBehavior('');
            setIssueFrequency('sometimes');
            setReproSteps('');
            setContactEmail('');
            setReportSending(false);
            setReportError(null);
            setReportSent(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (drawerScreen === 'menu') {
            setReportSent(false);
            setReportError(null);
        }
    }, [drawerScreen]);

    const frequencyLabel =
        ISSUE_FREQUENCY[issueFrequency]?.label
        ?? ISSUE_FREQUENCY.sometimes.label;

    const buildReportMessage = () => {
        const parts = [
            `What you were trying to do:\n${tryingToDo.trim()}`,
            `What you expected:\n${expectedBehavior.trim()}`,
            `What happened instead:\n${actualBehavior.trim()}`,
            `How often:\n${frequencyLabel}`,
            `Steps to reproduce:\n${reproSteps.trim()}`,
        ];
        return parts.join('\n\n');
    };

    const handleReportSubmit = async (e) => {
        e.preventDefault();
        setReportError(null);
        setReportSending(true);
        try {
            await submitIssueReport({ email: contactEmail, message: buildReportMessage() });
            setReportSent(true);
            setTryingToDo('');
            setExpectedBehavior('');
            setActualBehavior('');
            setIssueFrequency('sometimes');
            setReproSteps('');
            setContactEmail('');
        } catch (err) {
            setReportError(err.message || 'Could not send. Try again later.');
        } finally {
            setReportSending(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50">
            <div
                className={`absolute inset-0 bg-black transition-opacity duration-300 ${isAnimating ? 'bg-opacity-30 opacity-100' : 'bg-opacity-0 opacity-0'}`}
                onClick={onClose}
                role="presentation"
            />
            <div className={`absolute top-0 left-0 h-full w-64 bg-white shadow-xl transform transition-transform duration-300 ease-out flex flex-col ${isAnimating ? 'translate-x-0' : '-translate-x-full'}`}>
                {drawerScreen === 'menu' ? (
                    <>
                        <div className="p-4 border-b flex items-center justify-between shrink-0">
                            <a
                                href="https://pi-schedule.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="relative flex items-center decoration-transparent hover:opacity-80 transition-opacity"
                            >
                                <img src={logo} alt="Pi-Schedule Logo" className="w-10 h-10 drop-shadow-sm" />
                                <div className="absolute inset-0 w-10 h-10 bg-blue-100/30 rounded-full blur-md pointer-events-none" aria-hidden="true"></div>
                                <span className="text-xl font-light text-slate-800 tracking-tight ml-[-4px] relative z-10">
                                    Schedule
                                </span>
                            </a>
                        </div>
                        <div className="p-2 space-y-1 flex-1 min-h-0">
                            <button
                                type="button"
                                className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 ${timeMode !== 'occupancy' && timeMode !== 'events' ? 'bg-gray-200 text-gray-900' : 'hover:bg-gray-100 text-gray-700'}`}
                                onClick={() => onSelectPrimaryView('calendar')}
                            >
                                <HiCalendarDays className="h-5 w-5" />
                                <span>Calendar</span>
                            </button>
                            <button
                                type="button"
                                className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 ${timeMode === 'events' ? 'bg-gray-200 text-gray-900' : 'hover:bg-gray-100 text-gray-700'}`}
                                onClick={() => onSelectPrimaryView('events')}
                            >
                                <IoMdMicrophone className="h-5 w-5" />
                                <span>Events</span>
                            </button>
                            <button
                                type="button"
                                className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 ${timeMode === 'occupancy' ? 'bg-gray-200 text-gray-900' : 'hover:bg-gray-100 text-gray-700'}`}
                                onClick={() => onSelectPrimaryView('occupancy')}
                            >
                                <HiBuildingOffice className="h-5 w-5" />
                                <span>Rooms Occupancy</span>
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="p-4 border-b flex items-center gap-2 shrink-0">
                            <button
                                type="button"
                                className="p-1 rounded-md text-gray-600 hover:text-blue-600 shrink-0"
                                onClick={() => setDrawerScreen('menu')}
                                aria-label="Back to views"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            </button>
                            <span className="text-lg font-semibold text-gray-800">Report issue</span>
                            <button
                                type="button"
                                className="ml-auto p-1 rounded-md text-gray-600 hover:text-blue-600"
                                onClick={onClose}
                                aria-label="Close menu"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        <form onSubmit={handleReportSubmit} className="flex-1 min-h-0 flex flex-col p-4 gap-3 overflow-y-auto">
                            {reportSent && (
                                <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2" role="status">
                                    Thanks — your report was sent.
                                </p>
                            )}
                            {reportError && (
                                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" role="alert">
                                    {reportError}
                                </p>
                            )}
                            <p className="text-xs text-gray-500 -mt-1">
                                These questions help a developer narrow down and fix the issue.
                            </p>
                            <div>
                                <label htmlFor="report-trying" className="block text-sm font-medium text-gray-700 mb-1">
                                    What were you trying to do?
                                </label>
                                <textarea
                                    id="report-trying"
                                    name="tryingToDo"
                                    value={tryingToDo}
                                    onChange={(e) => setTryingToDo(e.target.value)}
                                    required
                                    disabled={reportSending}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm resize-y min-h-[72px] disabled:bg-gray-100"
                                    placeholder="e.g. I was moving a class to another day…"
                                />
                            </div>
                            <div>
                                <label htmlFor="report-expected" className="block text-sm font-medium text-gray-700 mb-1">
                                    What did you expect to happen?
                                </label>
                                <textarea
                                    id="report-expected"
                                    name="expected"
                                    value={expectedBehavior}
                                    onChange={(e) => setExpectedBehavior(e.target.value)}
                                    required
                                    disabled={reportSending}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm resize-y min-h-[72px] disabled:bg-gray-100"
                                    placeholder="e.g. The class should show on Tuesday…"
                                />
                            </div>
                            <div>
                                <label htmlFor="report-actual" className="block text-sm font-medium text-gray-700 mb-1">
                                    What actually happened?
                                </label>
                                <textarea
                                    id="report-actual"
                                    name="actual"
                                    value={actualBehavior}
                                    onChange={(e) => setActualBehavior(e.target.value)}
                                    required
                                    disabled={reportSending}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm resize-y min-h-[72px] disabled:bg-gray-100"
                                    placeholder="e.g. It disappeared after I changed week…"
                                />
                            </div>
                            <div>
                                <span id="report-frequency-label" className="block text-sm font-medium text-gray-700 mb-2">
                                    How often does this happen?
                                </span>
                                <div
                                    className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 gap-0.5"
                                    role="group"
                                    aria-labelledby="report-frequency-label"
                                >
                                    {Object.values(ISSUE_FREQUENCY).map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            disabled={reportSending}
                                            onClick={() => setIssueFrequency(opt.value)}
                                            className={`flex-1 min-w-0 rounded-md px-1.5 py-2 text-xs font-medium transition-colors ${issueFrequency === opt.value
                                                ? 'bg-white text-blue-700 shadow-sm ring-1 ring-gray-200'
                                                : 'text-gray-600 hover:bg-gray-100'
                                                } disabled:opacity-60`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label htmlFor="report-repro" className="block text-sm font-medium text-gray-700 mb-1">
                                    What are the steps to reproduce the issue?
                                </label>
                                <textarea
                                    id="report-repro"
                                    name="reproSteps"
                                    value={reproSteps}
                                    onChange={(e) => setReproSteps(e.target.value)}
                                    required
                                    disabled={reportSending}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm resize-y min-h-[72px] disabled:bg-gray-100"
                                    placeholder="e.g. Open Events → pick another week → …"
                                />
                            </div>
                            <div>
                                <Input
                                    id="report-issue-email"
                                    name="contactEmail"
                                    type="email"
                                    label="Your email"
                                    value={contactEmail}
                                    onChange={(e) => setContactEmail(e.target.value)}
                                    required
                                    disabled={reportSending}
                                    autoComplete="email"
                                    placeholder="you@example.com"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={reportSending}
                                className="mt-auto w-full py-2 px-3 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {reportSending ? 'Sending…' : 'Send'}
                            </button>
                        </form>
                    </>
                )}

                {drawerScreen === 'menu' && (
                    <div className="mt-auto shrink-0 p-4 border-t border-gray-100 space-y-1">
                        <button
                            type="button"
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                            onClick={() => setDrawerScreen('reportIssue')}
                        >
                            <HiExclamationTriangle className="h-4 w-4 shrink-0" />
                            <span>Report issue</span>
                        </button>
                        {onChangeInstitution && (
                            <button
                                type="button"
                                className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                onClick={onChangeInstitution}
                            >
                                <HiArrowRightOnRectangle className="h-4 w-4 shrink-0" />
                                <span>Change institution</span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
